// SlidePlayer.jsx

import React, {
  useState, useRef, useEffect, useCallback, useLayoutEffect
} from 'react';
import { renderSlideToCanvas } from '../canvas/SlideCanvasRenderer';

import './SlidePlayer.css';

const CANVAS_W = 1920;
const CANVAS_H = 1080;
const SCALE = 2;
const AUTOPLAY_INTERVAL_MS = 5000; // ~5s per slide during slideshow playback

export default function SlidePlayer({
  processedSlides,
  slideTemplates,
  slideImages,
  getSlideImageUrl,
  fontsLoaded,
  getCurrentTemplate,
  getFilteredTemplatesForSlide,
  handleTemplatePrev,
  handleTemplateNext,
  handleLayoutFilterChange,
  getLayoutFilter,
  handleImageUpload,
  currentSlideIndex: externalSlideIndex,
  onCurrentSlideChange,
}) {
  // ── slide state ──────────────────────────────────────────────
  const [currentSlideIndex, setCurrentSlideIndex] = useState(externalSlideIndex ?? 0);
  const [isRendering,       setIsRendering]        = useState(false);
  const [uploadingImage,    setUploadingImage]      = useState(false);

  // ── playback state ───────────────────────────────────────────
  const [isPlaying, setIsPlaying] = useState(false);

  // ── refs ─────────────────────────────────────────────────────
  const canvasRef        = useRef(null);
  const offscreenCanvasRef = useRef(null); // used to render off-screen so the
                                            // visible canvas is never repainted
                                            // mid-draw (that was the flicker/jank)
  const fileInputRef     = useRef(null);

  const isPlayingRef       = useRef(false);
  const currentSlideIndexRef = useRef(externalSlideIndex ?? 0);
  const autoplayTimerRef   = useRef(null);

  // slide rendering dedup
  const lastRenderedKeyRef  = useRef('');

  const totalSlides = processedSlides.length;

  // ── canvas setup ─────────────────────────────────────────────
  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width  = CANVAS_W * SCALE;
    canvas.height = CANVAS_H * SCALE;
    const ctx = canvas.getContext('2d', { alpha: false });
    // No ctx.scale() here anymore — renderSlide() draws a fully-finished
    // frame on an off-screen canvas and blits it in with drawImage, so the
    // visible canvas's own transform just stays identity.
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
  }, []);

  useEffect(() => {
    currentSlideIndexRef.current = currentSlideIndex;
  }, [currentSlideIndex]);

  // ── stay in sync when the active slide is changed from OUTSIDE this
  // component — e.g. the user clicks a thumbnail in SlideThumbnailRail.
  // Internally-triggered changes (prev/next/autoplay) already update the
  // parent via onCurrentSlideChange below, so this is a no-op in that case.
  useEffect(() => {
    if (externalSlideIndex === undefined || externalSlideIndex === null) return;
    if (externalSlideIndex !== currentSlideIndexRef.current) {
      setCurrentSlideIndex(externalSlideIndex);
    }
  }, [externalSlideIndex]);

  // ── slide render ─────────────────────────────────────────────
  const renderSlide = useCallback(async (slideIndex) => {
    if (!canvasRef.current || !fontsLoaded) return;
    const slide = processedSlides[slideIndex];
    if (!slide) return;
    const templateId = slideTemplates[slideIndex] || 'modern_bullets_right';
    const imageUrl   = getSlideImageUrl(slideIndex);
    const contentKey = JSON.stringify(slide.extractedContent || {});
    const key        = `${slideIndex}|${templateId}|${imageUrl}|${contentKey}`;
    if (lastRenderedKeyRef.current === key) return;
    lastRenderedKeyRef.current = key;

    // Only show the rendering spinner if this actually takes a moment
    // (e.g. loading a new, not-yet-cached image). A cached re-render
    // (just switching template on the same slide) finishes well under
    // this, so the spinner never flashes and the swap feels instant.
    const spinnerTimer = setTimeout(() => setIsRendering(true), 120);

    try {
      if (!offscreenCanvasRef.current) {
        offscreenCanvasRef.current = document.createElement('canvas');
      }
      const offscreen = offscreenCanvasRef.current;

      // Draw the whole frame off-screen first...
      await renderSlideToCanvas(offscreen, slide.extractedContent, templateId, imageUrl);

      // ...a newer render may have started while we were awaiting this one
      // (fast template clicking) — if so, drop this stale result.
      if (lastRenderedKeyRef.current !== key) return;

      const visible = canvasRef.current;
      if (!visible) return;

      // ...then blit the finished bitmap onto the visible canvas in one
      // paint. The visible canvas is never touched mid-draw, so there's no
      // blank/half-drawn frame flashing on screen during the transition.
      const ctx = visible.getContext('2d', { alpha: false });
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.drawImage(offscreen, 0, 0, offscreen.width, offscreen.height, 0, 0, visible.width, visible.height);
    } catch (e) {
      console.error('SlidePlayer slide render error', e);
    } finally {
      clearTimeout(spinnerTimer);
      setIsRendering(false);
    }
  }, [processedSlides, slideTemplates, getSlideImageUrl, fontsLoaded]);

  // ── re-render slide when template/image/content changes ───────
  const currentImageUrl = slideImages?.[currentSlideIndex]?.image?.url ?? null;
  const currentSlideContent = processedSlides[currentSlideIndex]?.extractedContent;
  const currentContentKey = currentSlideContent ? JSON.stringify(currentSlideContent) : '';
  useEffect(() => {
    lastRenderedKeyRef.current = '';
    renderSlide(currentSlideIndex);
  }, [
    currentSlideIndex,
    fontsLoaded,
    slideTemplates[currentSlideIndex],
    currentImageUrl,
    currentContentKey,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── let the parent page know which slide is currently showing ──
  useEffect(() => {
    if (onCurrentSlideChange) onCurrentSlideChange(currentSlideIndex);
  }, [currentSlideIndex, onCurrentSlideChange]);

  // ── controls ─────────────────────────────────────────────────
  const pause = useCallback(() => {
    isPlayingRef.current = false;
    setIsPlaying(false);
    if (autoplayTimerRef.current) {
      clearInterval(autoplayTimerRef.current);
      autoplayTimerRef.current = null;
    }
  }, []);

  const goToSlide = useCallback((index) => {
    setCurrentSlideIndex(Math.max(0, Math.min(totalSlides - 1, index)));
  }, [totalSlides]);

  const goPrev = useCallback(() => {
    if (isPlayingRef.current) pause();
    goToSlide(currentSlideIndexRef.current - 1);
  }, [pause, goToSlide]);

  const goNext = useCallback(() => {
    if (isPlayingRef.current) pause();
    goToSlide(currentSlideIndexRef.current + 1);
  }, [pause, goToSlide]);

  // ── slideshow autoplay ──────────────────────────────────────────
  const play = useCallback(() => {
    if (!fontsLoaded || totalSlides === 0) return;
    // Starting from the last slide restarts the deck from the top.
    if (currentSlideIndexRef.current >= totalSlides - 1) {
      setCurrentSlideIndex(0);
    }
    isPlayingRef.current = true;
    setIsPlaying(true);

    autoplayTimerRef.current = setInterval(() => {
      const next = currentSlideIndexRef.current + 1;
      if (next >= totalSlides) {
        pause();
        return;
      }
      setCurrentSlideIndex(next);
    }, AUTOPLAY_INTERVAL_MS);
  }, [fontsLoaded, totalSlides, pause]);

  const togglePlay = useCallback(() => {
    if (isPlayingRef.current) {
      pause();
    } else {
      play();
    }
  }, [pause, play]);

  // ── cleanup on unmount ────────────────────────────────────────
  useEffect(() => () => {
    if (autoplayTimerRef.current) clearInterval(autoplayTimerRef.current);
  }, []);

  // ── image upload ──────────────────────────────────────────────
  const withPause = useCallback((fn) => { if (isPlayingRef.current) pause(); fn(); }, [pause]);
  const handlePrevTemplate = () => withPause(() => handleTemplatePrev(currentSlideIndex));
  const handleNextTemplate = () => withPause(() => handleTemplateNext(currentSlideIndex));
  const handleLayoutBtn    = (l) => withPause(() => handleLayoutFilterChange(currentSlideIndex, l));

  const handleFileSelect = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    if (isPlayingRef.current) pause();
    setUploadingImage(true);
    try { await handleImageUpload(currentSlideIndex, file); }
    catch (err) { alert(`Failed to upload image: ${err.message}`); }
    finally { setUploadingImage(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const layoutFilter    = getLayoutFilter(currentSlideIndex);
  const currentTemplate = getCurrentTemplate(currentSlideIndex);
  const filteredList    = getFilteredTemplatesForSlide(currentSlideIndex);
  const currentIdx      = filteredList.findIndex(t => t?.id === currentTemplate?.id);

  if (!totalSlides) return null;

  return (

     
    <div className="up-container">
      {/* ── Canvas ── */}
      <div className="temp-switch">
        {/* ── Template switch bar ── */}
      
        <div className="up-template-bar">
          <button className="up-arrow" onClick={handlePrevTemplate} disabled={filteredList.length <= 1}>‹</button>
          <div className="up-template-pill">
            {currentTemplate?.name || ''}
            <span className="up-template-count">
              {currentIdx >= 0 ? currentIdx + 1 : 0} / {filteredList.length}
            </span>
          </div>
          <button className="up-arrow" onClick={handleNextTemplate} disabled={filteredList.length <= 1}>›</button>
        </div>
      
      </div>
     
      <div className="up-canvas-wrapper">
        <canvas ref={canvasRef} className="up-canvas" />

        {isRendering && (
          <div className="up-render-overlay"><div className="up-spinner" /></div>
        )}
      </div>

      

      {/* ── Image buttons ── */}
      <div className="up-image-buttons">
        <button className={`up-img-btn ${layoutFilter === 'left'  ? 'active' : ''}`} onClick={() => handleLayoutBtn('left')}  disabled={!fontsLoaded || uploadingImage}>Image Left</button>
        <button className={`up-img-btn ${layoutFilter === 'none'  ? 'active' : ''}`} onClick={() => handleLayoutBtn('none')}  disabled={!fontsLoaded || uploadingImage}>No Image</button>
        <button className={`up-img-btn ${layoutFilter === 'right' ? 'active' : ''}`} onClick={() => handleLayoutBtn('right')} disabled={!fontsLoaded || uploadingImage}>Image Right</button>
        <button className="up-img-btn up-img-upload" onClick={() => { if (isPlayingRef.current) pause(); fileInputRef.current?.click(); }} disabled={!fontsLoaded || uploadingImage}>
          {uploadingImage ? <><span className="up-upload-spinner">⟳</span> Uploading…</> : 'Upload Image'}
        </button>
        <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileSelect} />
      </div>

      {/* ── Controls ── */}
      <div className="up-controls">
        <div className="up-controls-row">
          <button
            className="up-btn up-btn-nav up-btn-prev"
            onClick={goPrev}
            disabled={currentSlideIndex === 0}
            aria-label="Previous slide"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>

          <button
            className={`up-btn up-btn-play ${isPlaying ? 'is-playing' : ''}`}
            onClick={togglePlay}
            disabled={!fontsLoaded}
            aria-label={isPlaying ? 'Pause slideshow' : 'Play slideshow'}
          >
            {isPlaying
              ? <svg viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>
              : <svg viewBox="0 0 24 24" fill="currentColor"><polygon points="5,3 19,12 5,21"/></svg>
            }
          </button>

          <button
            className="up-btn up-btn-nav up-btn-next"
            onClick={goNext}
            disabled={currentSlideIndex === totalSlides - 1}
            aria-label="Next slide"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </div>

        <div className="up-slide-count">Slide {currentSlideIndex + 1} of {totalSlides}</div>
      </div>
    </div>
  );
}